import empty from "is-empty";
import { store } from "../index";
import { AuthSelectors } from "../redux/Auth/selectors";
import { LOCAL_STORAGE_DEFAULT_VALUES } from "../redux/Tx/constants";
import { TopBarSelectors } from "../redux/TopBar/selectors";

export const STORAGE_KEYS = Object.freeze({
    CONFIRMATIONS: "CONFIRM_LIST_TABLE",
    TRANSACTIONS: "TX_LIST_TABLE",
    CONFIRMATION_SETTINGS: "CONFIRM_SETTINGS",
    AVAILABILITIES: "AVAILABILITIES_TABLE",
    AVAILABILITIES_FILTER: "AVAILABILITIES_FILTER",
    OFFER_TXS: "OFFER_TXS",
    INVENTORY_COLUMNS: "INVENTORY_COLUMN_STATE",
});

export const STORAGE_VERSIONS = Object.freeze({
    CONFIRM_LIST_TABLE: "6",
    TX_LIST_TABLE: "1",
    CONFIRM_SETTINGS: "3",
    AVAILABILITIES_TABLE: "3",
    AVAILABILITIES_FILTER: "3",
    OFFER_TXS: "2",
});

export const storageKey = (key) => {
    const role = TopBarSelectors.Role(store.getState());
    return `${key}.${role}.v${STORAGE_VERSIONS[key]}`;
};

export const getItem = (key) => {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
};

export const removeItem = (key) => {
    localStorage.removeItem(key);
};

export const setItem = (key, value) => {
    localStorage.setItem(key, JSON.stringify(value));
};

export const setLocalStorage = (key, fieldName, value) => {
    const itemKey = storageKey(key);
    try {
        if (fieldName) {
            let currentValue = getItem(itemKey);
            if (!currentValue) currentValue = {};
            currentValue[fieldName] = value;
            setItem(itemKey, currentValue);
        } else {
            setItem(itemKey, value);
        }
    } catch (e) {
        console.error(`setLocalStorage: ${key}-${fieldName} error`, e);
    }
};

export const getLocalStorage = (key, fieldName) => {
    try {
        let currentValue = getItem(storageKey(key));
        if (fieldName) {
            return currentValue ? currentValue[fieldName] : null;
        }
        return currentValue;
    } catch (e) {
        console.error(`getLocalStorage: ${key}-${fieldName} error`, e);
    }
};

// For saving AC value
export const saveLocalUserStorage = (key, value) => {
    const user = AuthSelectors.User(store.getState());
    if (user.username) {
        setItem(`${key}_${user.username}`, value);
    }
};

export const removeLocalUserStorage = (key) => {
    const user = AuthSelectors.User(store.getState());
    if (user.username) {
        removeItem(`${key}_${user.username}`);
    }
};

export const getFromLocalUserStorage = (key) => {
    const user = AuthSelectors.User(store.getState());
    const item = getItem(`${key}_${user.username}`);
    const defaultObj = LOCAL_STORAGE_DEFAULT_VALUES[key];

    if (
        empty(item) ||
        Object.keys(item).length !== Object.keys(defaultObj).length
    ) {
        // Check incomplete value
        return { ...defaultObj };
    }

    return item;
};

// for saving ag-grid table api
export const getTableInfo = (
    tableType,
    columnApi,
    defaultSortModel = [],
    aggInfo = {}
) => {
    if (!columnApi) return;
    try {
        let columnState = getLocalStorage(tableType, null);
        const hasCurrentSortState =
            columnState && columnState.some((s) => s.sort);

        if (!columnState) {
            columnState = columnApi.getColumnState();
        }

        const updatedColumnState = columnState.map((column) => {
            const defaultModel = !hasCurrentSortState
                ? defaultSortModel.find((m) => m.colId === column.colId)
                : null;

            let aggFunc = column.aggFunc;
            if (Object.keys(aggInfo).length > 0) {
                aggFunc =
                    tableType === STORAGE_KEYS.TRANSACTIONS
                        ? "aggOrigin"
                        : null;

                if (aggInfo[column.colId]) {
                    aggFunc = aggInfo[column.colId];
                }
            }

            return {
                ...column,
                aggFunc,
                sort: defaultModel ? defaultModel.sort : column.sort,
                sortIndex:
                    defaultModel && "sortIndex" in defaultModel
                        ? defaultModel.sortIndex
                        : column.sortIndex,
            };
        });

        // --- Inject "Constituents" column to the very left if not already present ---
        const hasConstituents = updatedColumnState.some(
            (c) => c.colId === "used_constituents"
        );
        if (!hasConstituents) {
            updatedColumnState.unshift({
                colId: "used_constituents",
                hide: false,
                pinned: null,
                aggFunc: null,
            });
        }

        columnApi.applyColumnState({
            state: updatedColumnState,
            applyOrder: true,
        });
    } catch (e) {
        console.error(`getTableInfo: ${tableType} table error`, e);
    }
};

export const setTableInfo = (tableType, columnApi) => {
    if (!columnApi) return;
    try {
        setLocalStorage(tableType, null, columnApi.getColumnState());
    } catch (e) {
        console.error(`setTableInfo: ${tableType} table error`, e);
    }
};

export const setFilterInfo = (tableType, api) => {
    if (!api) return;
    try {
        setLocalStorage(tableType, null, api.getFilterModel());
    } catch (e) {
        console.error(`setFilterInfo: ${tableType} table error`, e);
    }
};

export const getFilterInfo = (tableType, api) => {
    if (!api) return;
    try {
        const filterInfo = getLocalStorage(tableType, null);
        if (!empty(filterInfo)) {
            api.setFilterModel(filterInfo);
        }
    } catch (e) {
        console.error(`getFilterInfo: ${tableType} table error`, e);
    }
};
